import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  Sparkles,
  Wand2,
  Camera,
  UploadCloud,
  RotateCcw,
  Trash2,
  Download,
  ShoppingBag,
  Loader2,
  Check,
  AlertTriangle,
  ImageOff,
} from "lucide-react";
import { API } from "../../constants/config";
import { T } from "../../constants/theme";
import { LOCAL_MAIN_IMAGE } from "../../constants/images";
import { CartContext } from "../../contexts/CartContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { requireLogin } from "../../utils/authHelpers";

const LOADING_MESSAGES = [
  "Preparing your growing space...",
  "Placing selected plants and seeds...",
  "Matching site conditions...",
  "Generating realistic growth...",
  "Applying natural lighting...",
  "Rendering final preview...",
];

const TRYON_MESSAGES_KEY = "greenblessVirtualTryOnMessages";
const TRYON_SESSION_KEY = "greenblessVirtualTryOnSessionId";

const GROWTH_STAGES = [
  { id: "just_planted", label: "Just Planted" },
  { id: "sprouting", label: "Sprouting" },
  { id: "growing", label: "Growing" },
  { id: "harvest_ready", label: "Harvest Ready" },
];

const toDataUrl = (value) => {
  if (!value) return null;
  return value.startsWith?.("data:")
    ? value
    : `data:image/png;base64,${value}`;
};

const normalizeViews = (data) => {
  const source = data?.views || {};
  const normalized = {};

  GROWTH_STAGES.forEach((view) => {
    if (source[view.id]) {
      normalized[view.id] = toDataUrl(source[view.id]);
    }
  });

  if (!normalized.front && data?.image_b64) {
    normalized.front = toDataUrl(data.image_b64);
  }

  return normalized;
};


const DEFAULT_MESSAGES = [
  {
    role: "assistant",
    text: "Your garden visualization is ready. Describe any changes you would like to update.",
  },
];

export default function VirtualTryOnPage() {
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const { items: ctxItems, addItem, setOpen } = useContext(CartContext) || {};
  const stripe = useStripe();
  const elements = useElements();
   const {
    items,
    total,
    count,
    open,
    updateItem,
    cartId,
    cartToken,
    refreshCart,
  } = useContext(CartContext);


  const [payload, setPayload] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("virtualTryonPayload") || "{}") || {};
    } catch (e) {
      return {};
    }
  });
const [messages, setMessages] = useState(() => {
  try {
    const saved = sessionStorage.getItem(TRYON_MESSAGES_KEY);
    const parsed = saved ? JSON.parse(saved) : null;

    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch (error) {
    console.warn("Could not restore try-on conversation", error);
  }

  return DEFAULT_MESSAGES;
});

const chatEndRef = useRef(null);
  const [siteDetails, setSiteDetails] = useState(payload.siteDetails || { plot_size: "", sunlight: "", soil_type: "" });
  const [results, setResults] = useState(payload.results || []);
  const [views, setViews] = useState(() => {
    const restored = payload.views || {};
    const normalized = {};
    Object.entries(restored).forEach(([key, value]) => {
      const dataUrl = toDataUrl(value);
      if (dataUrl) normalized[key] = dataUrl;
    });
    return normalized;
  });
  const [activeStage, setActiveStage] = useState("just_planted");
  const [failedViews, setFailedViews] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [genError, setGenError] = useState(null);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return sessionStorage.getItem(TRYON_SESSION_KEY);
    } catch {
      return null;
    }
  });
  const photoInputRef = useRef();
  const viewerDragRef = useRef({
    dragging: false,
    startX: 0,
    startIndex: 0,
  });

  const [showTryOnPayment, setShowTryOnPayment] = useState(false);
  const [tryOnPaid, setTryOnPaid] = useState(false);
  const [refinementPrompt, setRefinementPrompt] = useState("");
  const [itemImages, setItemImages] = useState({});
  const [imgLoaded, setImgLoaded] = useState({});
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    try {
      sessionStorage.setItem(
        TRYON_MESSAGES_KEY,
        JSON.stringify(messages)
      );
    } catch (error) {
      console.warn("Could not save try-on conversation", error);
    }
  }, [messages]);

  useEffect(() => {
    try {
      if (sessionId) {
        sessionStorage.setItem(TRYON_SESSION_KEY, sessionId);
      } else {
        sessionStorage.removeItem(TRYON_SESSION_KEY);
      }
    } catch (error) {
      console.warn("Could not save try-on session", error);
    }
  }, [sessionId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 50);

    return () => window.clearTimeout(timer);
  }, [messages, editing]);

  // Auto-select every item the first time it appears in the cart.
  useEffect(() => {
    if (!ctxItems || !ctxItems.length) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      ctxItems.forEach((it) => {
        if (!seenIdsRef.current.has(it.id)) {
          seenIdsRef.current.add(it.id);
          next.add(it.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [ctxItems]);

  useEffect(() => {
    if (!loading) {
      setLoadingMsgIndex(0);
      return;
    }
    const id = setInterval(() => {
      setLoadingMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 1800);
    return () => clearInterval(id);
  }, [loading]);

  useEffect(() => {
    if (!ctxItems || !ctxItems.length) return;

    let isMounted = true;

    const loadImages = async () => {
      const results = await Promise.all(
        ctxItems.map(async (it) => {
          try {
            const res = await apiFetch(`/images/product/${it.product_id}`);
            if (!res.ok) return [it.product_id, null];
            const data = await res.json();
            return [it.product_id, data?.url || null];
          } catch {
            return [it.product_id, null];
          }
        })
      );

      if (isMounted) {
        setItemImages(Object.fromEntries(results));
      }
    };

    loadImages();

    return () => {
      isMounted = false;
    };
  }, [ctxItems]);

  const activeImage =
    views[activeStage] ||
    views.just_planted ||
    results?.[0]?.image ||
    null;

  const changeStage = (angleId) => {
    if (views[angleId]) {
      setActiveStage(angleId);
    }
  };

  const rotateViewer = (direction) => {
    const currentIndex = GROWTH_STAGES.findIndex(
      (view) => view.id === activeStage
    );

    for (let step = 1; step <= GROWTH_STAGES.length; step += 1) {
      const nextIndex =
        (currentIndex + direction * step + GROWTH_STAGES.length) %
        GROWTH_STAGES.length;
      const nextStage = GROWTH_STAGES[nextIndex].id;

      if (views[nextStage]) {
        setActiveStage(nextStage);
        return;
      }
    }
  };

  const handleViewerPointerDown = (event) => {
    viewerDragRef.current = {
      dragging: true,
      startX: event.clientX,
      startIndex: GROWTH_STAGES.findIndex(
        (view) => view.id === activeStage
      ),
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleViewerPointerUp = (event) => {
    const drag = viewerDragRef.current;

    if (!drag.dragging) return;

    drag.dragging = false;
    const distance = event.clientX - drag.startX;

    if (Math.abs(distance) < 40) return;

    rotateViewer(distance < 0 ? 1 : -1);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = (ctxItems || []).filter((it) => selectedIds.has(it.id));

  const validateSiteDetails = () => {
  const { plot_size, sunlight } = siteDetails;

  if (!plot_size || !sunlight) {
    toast("Please fill in plot size and sunlight", "error");
    return false;
  }

  const size = Number(plot_size);

  if (isNaN(size) || size <= 0) {
    toast("Plot size must be a valid number", "error");
    return false;
  }

  return true;
};

  const generate = async () => {
    if (!photo) {
      toast("Please upload a photo of your growing space first", "error");
      return;
    }
    setLoading(true);
    setGenError(null);
    toast("Generation started", "info");
    try {
      const form = new FormData();
      form.append("photo", photo);
      form.append("cart_id", cartId);
      form.append("plot_size", siteDetails.plot_size);
      form.append("sunlight", siteDetails.sunlight);
      form.append("soil_type", siteDetails.soil_type || "");
      form.append(
        "selected_item_ids",
        JSON.stringify(selectedItems.map((it) => it.product_id || it.id))
      );

      const res = await fetch(`${API}/agent/try-on/start`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Generation failed");

      const generatedViews = normalizeViews(data);

      if (!generatedViews.front) {
        throw new Error("The server did not return a front try-on image");
      }

      setSessionId(data.session_id);
      setViews(generatedViews);
      setActiveStage("just_planted");
      setFailedViews(data.failed_views || {});
      setResults([
        {
          image: generatedViews.front,
          message: data.message,
        },
      ]);
      const openingMessage = {
        role: "assistant",
        text:
          data.message ||
          "Your garden visualization is ready. Describe any changes you would like.",
      };

      setMessages((previous) => {
        const hasUserHistory = previous.some(
          (message) => message.role === "user"
        );

        return hasUserHistory
          ? [...previous, openingMessage]
          : [openingMessage];
      });

      setRefinementPrompt("");
      toast("Garden visualization completed");
    } catch (e) {
      console.error(e);
      setGenError(e.message || "Generation failed");
      toast(e.message || "Generation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const chatEdit = async (message) => {
    if (!sessionId) {
      throw new Error("Generate a try-on first");
    }

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      throw new Error("Please enter an edit request");
    }

    setEditing(true);
    setGenError(null);

    try {
      const res = await fetch(`${API}/agent/try-on/chat/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: trimmedMessage }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Edit failed");
      }

      const updatedViews = normalizeViews(data);

      if (updatedViews.front) {
        setViews(updatedViews);
        setActiveStage("just_planted");
        setFailedViews(data.failed_views || {});

        setResults((previous) => {
          const updated = [...previous];
          const nextResult = {
            image: updatedViews.front,
            message: data.message,
          };

          if (updated.length === 0) {
            return [nextResult];
          }

          updated[0] = {
            ...updated[0],
            ...nextResult,
          };

          return updated;
        });
      }

      toast("Garden visualization updated");
      return data;
    } finally {
      setEditing(false);
    }
  };

  const handleRefinement = async () => {
    const prompt = refinementPrompt.trim();

    if (!prompt || editing) {
      return;
    }

    setMessages((previous) => [
      ...previous,
      {
        role: "user",
        text: prompt,
      },
    ]);

    setRefinementPrompt("");

    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 50);

    try {
      const data = await chatEdit(prompt);

      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text:
            data?.message ||
            "Done. I applied that change while preserving your previous edits.",
        },
      ]);
    } catch (error) {
      console.error(error);

      const errorMessage =
        error?.message ||
        "I could not apply that change. Please try again.";

      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text: errorMessage,
          error: true,
        },
      ]);

      toast(errorMessage, "error");
    } finally {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 50);
    }
  };

 const handleGenerate = () => {
  if (selectedItems.length === 0) {
    toast("Please select at least one plant, seed, or produce item.", "warning");
    return;
  }
  if (!validateSiteDetails()) return;
  if (!photo) { toast("Please upload a photo of your growing space", "error"); return; }

  if (!tryOnPaid) {
    setShowTryOnPayment(true);
    return;
  }

  generate();
};

  const payTryOn = async () => {
    // Lightweight demo payment: reuse CardElement UI but simulate success
    try {
      setShowTryOnPayment(false);
      setTryOnPaid(true);
      toast("Payment successful — generating try-on...");
      await generate();
    } catch (e) {
      console.error(e);
      toast("Payment failed", "error");
    }
  };

  const addItemsToCart = async () => {
    if (!requireLogin(user, navigate, toast, "Please sign in to add items to your shopping bag.")) {
      return;
    }
    if (!payload.items || !payload.items.length) {
      toast("No items to add", "info");
      return;
    }
    for (const it of payload.items) {
      try {
        if (typeof addItem === "function") await addItem(it.id || it.product_id || it.productId, 1);
      } catch (e) {
        console.warn("addItem failed", e);
      }
    }
    toast("Items added to cart");
  };

  const purchaseKit = async () => {
    await addItemsToCart();
    try {
      sessionStorage.setItem("virtualTryonLaunchCheckout", "1");
    } catch (e) {
      console.warn("virtualTryon: unable to persist launch flag", e);
    }
    if (typeof setOpen === "function") setOpen(true);
  };

  const downloadResult = () => {
    if (!results || !results.length) return;
    const a = document.createElement("a");
    a.href = activeImage || results[0].image;
    a.download = `garden-visualization-${activeStage}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

   const styles = {
    page: {
      minHeight: "100vh",
      padding: "40px 24px",
      background: T.paper,
    },

    container: {
      maxWidth: 1450,
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 32,
      alignItems: "start",
    },

    card: {
      background: T.surface,
      backdropFilter: "blur(20px)",
      border: `1px solid rgba(95,203,140,.18)`,
      borderRadius: 28,
      padding: 32,
      boxShadow:
        "0 12px 32px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,.7)",
      transition: ".35s",
    },

 heading: {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 30,
  color: T.ink,
  marginBottom: 10,
  lineHeight: 1.15,
  fontWeight: 700,
},
  subHeading: {
  color: T.muted,
  fontSize: 16,
  lineHeight: 1.8,
  marginBottom: 40,
  maxWidth: 500,
},

    productCard: {
      display: "flex",
      alignItems: "center",
      gap: 18,
      padding: 18,
      borderRadius: 20,
      background: T.surface,
      border: "1px solid rgba(0,0,0,.06)",
      marginBottom: 14,
      transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
      boxShadow: "0 8px 20px rgba(0,0,0,.04)",
      cursor: "pointer",
      position: "relative",
    },

    imageWrap: {
      width: 85,
      height: 85,
      borderRadius: 18,
      position: "relative",
      flexShrink: 0,
      overflow: "hidden",
      boxShadow: "0 10px 25px rgba(0,0,0,.05)",
    },

    image: {
      width: 85,
      height: 85,
      objectFit: "cover",
      borderRadius: 18,
    },

    productTitle: {
      fontWeight: 700,
      fontSize: 16,
      color: T.ink,
      marginBottom: 6,
      letterSpacing: ".01em",
    },

    productPrice: {
      color: T.gold,
      fontWeight: 700,
    },

    qtyText: {
      color: T.muted,
      fontSize: 13,
      marginTop: 2,
    },

    sectionTitle: {
      fontSize: 22,
      fontWeight: 800,
      color: T.ink,
      marginTop: 30,
      marginBottom: 20,
    },

    inputGrid: {
      display: "grid",
      gap: 16,
    },

    input: {
      height: 58,
      borderRadius: 16,
      border: "1px solid rgba(0,0,0,.05)",
      background: T.paper,
      padding: "0 18px",
      outline: "none",
      fontSize: 15,
      boxShadow: "0 4px 15px rgba(0,0,0,.03)",
      transition: ".3s",
    },

    buttonRow: {
      display: "flex",
      gap: 16,
      marginTop: 28,
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes vtoShimmer {
          from { transform: translateX(-100%); }
          to { transform: translateX(100%); }
        }
        @keyframes vtoSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes vtoFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vtoPulseGlow {
          0%, 100% { opacity: .55; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        .vto-shimmer { position: relative; overflow: hidden; background: ${T.surface}; }
        .vto-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
          animation: vtoShimmer 1.4s infinite;
        }
        .vto-item-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(0,0,0,.05);
        }
        .vto-select-dot {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 2px solid rgba(0,0,0,.15);
          background: ${T.paper};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background .25s cubic-bezier(.4,0,.2,1), border-color .25s cubic-bezier(.4,0,.2,1), transform .25s cubic-bezier(.4,0,.2,1);
          margin-left: auto;
        }
        .vto-select-dot.checked {
          background: linear-gradient(135deg, ${T.goldHover} 0%, ${T.gold} 100%);
          border-color: ${T.gold};
          transform: scale(1.05);
        }
        .vto-select-dot svg {
          opacity: 0;
          transform: scale(.4);
          transition: opacity .2s ease, transform .2s ease;
          color: ${T.white};
        }
        .vto-select-dot.checked svg {
          opacity: 1;
          transform: scale(1);
        }
        .vto-remove-icon-btn {
          border: none;
          background: rgba(0,0,0,.04);
          color: ${T.muted};
          cursor: pointer;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background .2s ease, color .2s ease;
          flex-shrink: 0;
        }
        .vto-remove-icon-btn:hover {
          background: rgba(192,57,43,.12);
          color: #c0392b;
        }
        .vto-upload-box {
          position: relative;
          width: 100%;
          max-width: 320px;
          aspect-ratio: 3 / 4;
          margin: 0 auto 20px;
          border: 2px dashed rgba(95,203,140,.4);
          border-radius: 20px;
          background: ${T.paper};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          overflow: hidden;
          transition: border-color .25s ease, background .25s ease;
        }
        .vto-upload-box:hover {
          border-color: rgba(95,203,140,.7);
          background: ${T.surface};
        }
        .vto-premium-btn {
          border: none;
          background: ${T.gold};
          color: ${T.white};
          padding: 18px 56px;
          font-weight: 700;
          font-size: 14px;
          letter-spacing: .1em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all .3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-family: 'DM Sans', sans-serif;
          width: 100%;
        }
        .vto-premium-btn:hover:not(:disabled) {
          background: ${T.goldHover};
        }
        .vto-premium-btn:disabled {
          background: ${T.faint};
          cursor: not-allowed;
        }
        .vto-premium-btn.vto-btn-compact {
          padding: 11px 24px;
          font-size: 12px;
        }
        .vto-premium-btn.vto-btn-ghost {
          background: transparent;
          color: ${T.ink};
          border: 1px solid rgba(255,255,255,.16);
        }
        .vto-premium-btn.vto-btn-ghost:hover:not(:disabled) {
          background: ${T.ink};
          color: ${T.paper};
          border-color: ${T.ink};
        }
        .vto-spin { animation: vtoSpin 1s linear infinite; }
      `}</style>
      <div style={styles.container}>
        {/* LEFT PANEL */}
        <div style={styles.card}>
          <div style={styles.heading}>
            Garden Visualizer Studio
          </div>

          <div style={styles.subHeading}>
            See your garden grow, powered by AI.
          </div>

          {/* PRODUCTS */}
{(ctxItems || []).map((it, i) => {
  const checked = selectedIds.has(it.id);
  const loaded = !!imgLoaded[it.id];
  return (
    <div
      key={it.id || i}
      className="vto-item-card"
      style={{
        ...styles.productCard,
        borderColor: checked ? "rgba(95,203,140,.55)" : "rgba(0,0,0,.06)",
        boxShadow: checked ? "0 10px 26px rgba(95,203,140,.16)" : styles.productCard.boxShadow,
      }}
      onClick={() => toggleSelect(it.id)}
    >
      <div style={styles.imageWrap}>
        {!loaded && (
          <div className="vto-shimmer" style={{ position: "absolute", inset: 0, borderRadius: 18 }} />
        )}
        <img
          src={itemImages[it.product_id] || it.image || LOCAL_MAIN_IMAGE}
          alt={it.name || it.title || "product"}
          style={{ ...styles.image, opacity: loaded ? 1 : 0, transition: "opacity .3s ease" }}
          onLoad={() => setImgLoaded((m) => ({ ...m, [it.id]: true }))}
          onError={() => setImgLoaded((m) => ({ ...m, [it.id]: true }))}
        />
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={styles.productTitle}>
          {it.name || `Item ${it.product_id || i}`}
        </div>

        <div style={styles.productPrice}>
          LKR {Number(it.sale_price || it.price || 0).toFixed(2)}
        </div>

        <div style={styles.qtyText}>
          Qty: {it.qty}
        </div>
      </div>

      <button
        className="vto-remove-icon-btn"
        onClick={(e) => { e.stopPropagation(); updateItem(it.id, 0); }}
        aria-label="Remove item"
        title="Remove from bag"
      >
        <Trash2 size={14} />
      </button>

      <div className={`vto-select-dot${checked ? " checked" : ""}`} aria-label={checked ? "Selected" : "Not selected"}>
        <Check size={14} strokeWidth={3} />
      </div>
    </div>
  );
})}

          <div style={styles.sectionTitle}>
            Your Photo
          </div>

          <div
            className="vto-upload-box"
            onClick={() => photoInputRef.current.click()}
            style={{ background: T.paper }}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="Your photo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <div style={{ textAlign: "center", color: T.muted, padding: 20 }}>
                <Camera size={40} style={{ marginBottom: 10, color: T.gold }} />
                <div style={{ fontWeight: 600, fontSize: 14 }}>Click to upload a photo of your garden or growing space</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>JPEG, PNG or WebP · max 10 MB</div>
              </div>
            )}
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              setPhoto(file);
              const reader = new FileReader();
              reader.onload = (ev) => {
                setPhotoPreview(ev.target.result);
                toast("Photo uploaded successfully");
              };
              reader.readAsDataURL(file);
            }}
          />

          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <button
              className="vto-premium-btn vto-btn-compact vto-btn-ghost"
              onClick={() => photoInputRef.current.click()}
              style={{ width: "auto", flex: 1 }}
            >
              <UploadCloud size={15} />
              {photoPreview ? "Replace Photo" : "Upload Photo"}
            </button>
            {photoPreview && (
              <button
                className="vto-premium-btn vto-btn-compact vto-btn-ghost"
                onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                style={{ width: "auto", flex: 1 }}
              >
                <Trash2 size={15} />
                Remove Photo
              </button>
            )}
          </div>

          <div style={styles.sectionTitle}>
            Site Details
          </div>

          <div style={styles.inputGrid}>
            <input
              style={styles.input}
              placeholder="Plot Size (sq metres)"
              value={siteDetails.plot_size}
              onChange={(e) =>
                setSiteDetails((s) => ({
                  ...s,
                  plot_size: e.target.value,
                }))
              }
            />

            <select
              style={styles.input}
              value={siteDetails.sunlight}
              onChange={(e) =>
                setSiteDetails((s) => ({
                  ...s,
                  sunlight: e.target.value,
                }))
              }
            >
              <option value="">Sunlight...</option>
              <option value="full-sun">Full Sun</option>
              <option value="partial-shade">Partial Shade</option>
              <option value="full-shade">Full Shade</option>
            </select>

            <input
              style={styles.input}
              placeholder="Soil Type (optional)"
              value={siteDetails.soil_type}
              onChange={(e) =>
                setSiteDetails((s) => ({
                  ...s,
                  soil_type: e.target.value,
                }))
              }
            />
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
            <div style={styles.buttonRow}>
              <button
                className="vto-premium-btn"
                onClick={handleGenerate}
                disabled={loading || selectedItems.length === 0}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="vto-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Generate Try-On
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
<div
  style={{
    ...styles.card,
    position: "sticky",
    top: 30,
    overflow: "hidden",
  }}
>
  {/* Decorative glow */}
  <div
    style={{
      position: "absolute",
      width: 300,
      height: 300,
      borderRadius: "50%",
      background: "rgba(95,203,140,.08)",
      top: -100,
      right: -100,
      filter: "blur(80px)",
      pointerEvents: "none",
    }}
  />

  <div
    style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 30,
      color: T.ink,
      marginBottom: 10,
      lineHeight: 1.15,
      fontWeight: 700,
    }}
  >
    AI Preview Studio
  </div>

  <div
    style={{
      color: T.muted,
      fontSize: 16,
      lineHeight: 1.8,
      marginBottom: 40,
      maxWidth: 500,
    }}
  >
    See your garden brought to life with AI.
  </div>

  <div
    style={{
      borderRadius: 30,
      overflow: "hidden",
      background:
        `linear-gradient(145deg,${T.surface},${T.paper})`,
      minHeight: 500,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      border: "1px solid rgba(0,0,0,.05)",
      boxShadow:
        "0 25px 70px rgba(0,0,0,.05)",
      position: "relative",
    }}
  >
    {loading ? (
      <div style={{ padding: 50, textAlign: "center", width: "100%" }}>
        <div
          style={{
            width: 130,
            height: 130,
            margin: "0 auto 30px",
            borderRadius: "50%",
            background: `linear-gradient(135deg,${T.goldHover},${T.gold})`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 20px 60px rgba(95,203,140,.3)",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: -8,
              borderRadius: "50%",
              border: "2px solid rgba(95,203,140,.35)",
              animation: "vtoPulseGlow 1.8s ease-in-out infinite",
            }}
          />
          <Wand2 size={44} color={T.white} />
        </div>

        <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, marginBottom: 14 }}>
          Crafting Your Look
        </div>

        <div
          key={loadingMsgIndex}
          style={{
            color: T.muted,
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 26,
            animation: "vtoFadeIn .4s ease",
          }}
        >
          {LOADING_MESSAGES[loadingMsgIndex]}
        </div>

        <div style={{ maxWidth: 300, margin: "0 auto" }}>
          <div className="vto-shimmer" style={{ height: 10, borderRadius: 6, marginBottom: 10 }} />
          <div className="vto-shimmer" style={{ height: 10, width: "70%", borderRadius: 6, margin: "0 auto" }} />
        </div>
      </div>
    ) : genError ? (
      <div style={{ padding: 50, textAlign: "center" }}>
        <div
          style={{
            width: 110,
            height: 110,
            margin: "0 auto 26px",
            borderRadius: "50%",
            background: "#fdeceb",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AlertTriangle size={40} color="#c0392b" />
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, marginBottom: 10 }}>
          Generation Failed
        </div>
        <div style={{ color: T.muted, maxWidth: 380, margin: "0 auto 26px", lineHeight: 1.7 }}>
          {genError}
        </div>
        <button className="vto-premium-btn" style={{ width: "auto" }} onClick={handleGenerate}>
          <RotateCcw size={16} />
          Retry
        </button>
      </div>
    ) : results && results.length ? (
      <div
        style={{
          width: "100%",
          animation: "vtoFadeIn .5s ease",
        }}
      >
        {/* Multi-view rotation controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 10,
            padding: "18px 20px 0",
          }}
        >
          <button
            type="button"
            onClick={() => rotateViewer(-1)}
            disabled={Object.keys(views).length < 2}
            aria-label="Previous stage"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,.15)",
              background: T.surface,
              cursor:
                Object.keys(views).length < 2
                  ? "not-allowed"
                  : "pointer",
              opacity: Object.keys(views).length < 2 ? 0.35 : 1,
              fontWeight: 800,
            }}
          >
            ←
          </button>

          {GROWTH_STAGES.map((view) => {
            const available = Boolean(views[view.id]);
            const selected = activeStage === view.id;

            return (
              <button
                key={view.id}
                type="button"
                disabled={!available}
                onClick={() => changeStage(view.id)}
                style={{
                  padding: "9px 16px",
                  borderRadius: 999,
                  border: selected
                    ? `1px solid ${T.gold}`
                    : "1px solid rgba(0,0,0,.15)",
                  background: selected ? T.gold : T.surface,
                  color: selected ? T.white : T.ink,
                  fontWeight: 700,
                  cursor: available ? "pointer" : "not-allowed",
                  opacity: available ? 1 : 0.35,
                }}
                title={
                  available
                    ? `${view.label} view`
                    : failedViews[view.id] || `${view.label} view unavailable`
                }
              >
                {view.label}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => rotateViewer(1)}
            disabled={Object.keys(views).length < 2}
            aria-label="Next stage"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,.15)",
              background: T.surface,
              cursor:
                Object.keys(views).length < 2
                  ? "not-allowed"
                  : "pointer",
              opacity: Object.keys(views).length < 2 ? 0.35 : 1,
              fontWeight: 800,
            }}
          >
            →
          </button>
        </div>

        {/* Swipe/drag rotation viewer */}
        <div
          onPointerDown={handleViewerPointerDown}
          onPointerUp={handleViewerPointerUp}
          onPointerCancel={() => {
            viewerDragRef.current.dragging = false;
          }}
          style={{
            padding: 20,
            cursor: Object.keys(views).length > 1 ? "grab" : "default",
            touchAction: "pan-y",
            userSelect: "none",
          }}
        >
          <img
            src={activeImage}
            alt={`${activeStage} garden visualization view`}
            draggable={false}
            style={{
              width: "100%",
              height: 720,
              objectFit: "contain",
              background: T.paper,
              borderRadius: 24,
              display: "block",
              boxShadow: "0 25px 60px rgba(0,0,0,.06)",
              transition: "opacity .2s ease",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              textAlign: "center",
              color: T.muted,
              fontSize: 12,
              marginTop: 10,
            }}
          >
            Drag left or right, use the arrows, or select a growth stage.
          </div>
        </div>

        {/* AI Prompt */}
        <div
          style={{
            padding: "0 20px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: T.ink,
                fontSize: 16,
              }}
            >
              Refine Your Look
            </div>

            <button
              type="button"
              onClick={() => {
                setMessages(DEFAULT_MESSAGES);
                setRefinementPrompt("");

                try {
                  sessionStorage.removeItem(TRYON_MESSAGES_KEY);
                } catch (error) {
                  console.warn("Could not clear conversation history", error);
                }
              }}
              style={{
                border: "none",
                background: "transparent",
                color: T.muted,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                padding: 0,
              }}
            >
              Clear history
            </button>
          </div>

          {/* Conversation history */}
          <div
            style={{
              maxHeight: 340,
              minHeight: 150,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: 14,
              marginBottom: 14,
              borderRadius: 18,
              border: "1px solid rgba(0,0,0,.05)",
              background: T.paper,
              boxSizing: "border-box",
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  color: T.muted,
                  fontSize: 14,
                  textAlign: "center",
                  padding: "20px 8px",
                }}
              >
                Your previous edit commands will appear here.
              </div>
            )}

            {messages.map((message, index) => {
              const isUser = message.role === "user";

              return (
                <div
                  key={`${message.role}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "82%",
                      padding: "10px 14px",
                      borderRadius: isUser
                        ? "16px 16px 4px 16px"
                        : "16px 16px 16px 4px",
                      background: isUser
                        ? T.gold
                        : message.error
                          ? "#fff0f0"
                          : T.surface,
                      color: isUser
                        ? T.white
                        : message.error
                          ? "#a83232"
                          : T.ink,
                      border: isUser
                        ? "none"
                        : "1px solid rgba(0,0,0,.06)",
                      fontSize: 14,
                      lineHeight: 1.5,
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                      boxShadow: isUser
                        ? "none"
                        : "0 6px 18px rgba(0,0,0,.04)",
                    }}
                  >
                    {message.text || message.content}
                  </div>
                </div>
              );
            })}

            {editing && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: "16px 16px 16px 4px",
                    background: T.surface,
                    border: "1px solid rgba(0,0,0,.06)",
                    color: T.muted,
                    fontSize: 14,
                  }}
                >
                  <Loader2 size={14} className="vto-spin" />
                  Updating your garden…
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          <textarea
            value={refinementPrompt}
            onChange={(e) =>
              setRefinementPrompt(e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();

                if (!tryOnPaid) {
                  setShowTryOnPayment(true);
                } else {
                  handleRefinement();
                }
              }
            }}
            placeholder="Dream & Design..."
            disabled={editing}
            style={{
              width: "100%",
              minHeight: 100,
              resize: "vertical",
              padding: 18,
              borderRadius: 18,
              border: "1px solid rgba(0,0,0,.05)",
              background: T.paper,
              outline: "none",
              fontSize: 15,
              lineHeight: 1.6,
              boxShadow:
                "0 10px 25px rgba(0,0,0,.04)",
              boxSizing: "border-box",
            }}
          />

          <div
            style={{
              marginTop: 8,
              color: T.muted,
              fontSize: 12,
            }}
          >
            AI Can Make Mistakes, appearance may not align 100%.
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 18, marginTop: 24, flexWrap: "wrap" }}>
            <button
              className="vto-premium-btn"
              style={{ flex: 1, minWidth: 160, width: "auto" }}
              onClick={() => {
                if (!tryOnPaid) {
                  setShowTryOnPayment(true);
                } else {
                  handleRefinement();
                }
              }}
              disabled={editing || !refinementPrompt.trim()}
            >
              {editing ? (
                <>
                  <Loader2 size={16} className="vto-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Send
                </>
              )}
            </button>

            <button
              className="vto-premium-btn vto-btn-ghost"
              style={{ flex: 1, minWidth: 140, width: "auto" }}
              onClick={downloadResult}
            >
              <Download size={16} />
              Download
            </button>

            <button
              className="vto-premium-btn"
              style={{ flex: 1, minWidth: 160, width: "auto" }}
              onClick={purchaseKit}
            >
              <ShoppingBag size={16} />
              Purchase Kit
            </button>
          </div>
        </div>
      </div>
    ) : (
      <div
        style={{
          padding: 50,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 130,
            height: 130,
            margin: "0 auto 30px",
            borderRadius: "50%",
            background:
              `linear-gradient(135deg,${T.goldHover},${T.gold})`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow:
              "0 20px 60px rgba(95,203,140,.3)",
          }}
        >
          <ImageOff size={42} color={T.white} />
        </div>

        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 10,
          }}
        >
          No Preview Yet
        </div>

        <div
          style={{
            color: T.muted,
            maxWidth: 400,
            margin: "0 auto",
            lineHeight: 1.7,
          }}
        >
          Generate your first AI try-on and experience
          your garden in a premium visualization
          environment.
        </div>
      </div>
    )}
  </div>
</div>
      </div>

      {showTryOnPayment && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1200, display: "flex", justifyContent: "center", alignItems: "center", padding: 20 }}>
          <div style={{ width: "min(520px,100%)", background: T.surface, borderRadius: 14, padding: 20, boxShadow: "0 12px 32px rgba(0,0,0,0.05)" }}>
            <h3 style={{ marginTop: 0, color: T.ink }}>Garden Visualizer Payment</h3>
            <p style={{ marginTop: 0, color: T.muted }}>Amount: LKR 1.99</p>
            <div style={{ padding: "12px 0" }}>
              <CardElement />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="vto-premium-btn vto-btn-compact" style={{ width: "auto" }} onClick={payTryOn}>Pay LKR 1.99</button>
              <button className="vto-premium-btn vto-btn-compact vto-btn-ghost" style={{ width: "auto" }} onClick={() => setShowTryOnPayment(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}